import { validate as validatePostalCode } from "postal-codes-js";
import { type intlFormatPropTypes } from "react-intl";
import { z } from "zod";

import type { EnvCountries } from "../app-main/types/env.types";
import type { SelectOption } from "../app-main/types/general.types";

import { ZONE_EXCEPTION_CODES } from "./AddressForm.consts";
import {
  addressFormMessages,
  cityMessageByLabel,
  DEFAULT_CITY_LABEL,
  DEFAULT_POSTAL_CODE_LABEL,
  DEFAULT_ZONE_LABEL,
  postalCodeMessageByLabel,
  zoneMessageByLabel,
} from "./AddressForm.messages";
import type {
  AddressFieldNames,
  AddressFormMessage,
  Country,
} from "./AddressForm.types";

const findCountryByCode = (
  countries: Country[],
  countryCode: string,
): Country | undefined =>
  countries.find((country) => country.code === countryCode);

/**
 * `allCountries` is already localised. `envCountries` narrows the list to where
 * Peerspace operates, which suits a space but not an address merely reported to
 * us — a tax registration may sit anywhere. Omit it for every country.
 */
const buildCountryOptions = ({
  allCountries,
  envCountries,
}: {
  allCountries: Country[];
  envCountries?: EnvCountries;
}): SelectOption[] =>
  allCountries
    .filter((country) => !envCountries || envCountries[country.code]?.active)
    .map((country) => ({ value: country.code, label: country.name }));

/** Zone options for the selected country, minus codes Peerspace does not accept. */
const buildZoneOptions = (selectedCountry?: Country): SelectOption[] =>
  (selectedCountry?.zones ?? [])
    .filter((zone) => !ZONE_EXCEPTION_CODES.includes(zone.code))
    .map((zone) => ({ value: zone.code, label: zone.name }));

/** A value nobody filled in. Whitespace does not count as an answer. */
const isBlank = (value: string) => value.trim().length === 0;

/**
 * Case-insensitive on purpose: `@shopify/address` labels the US postal field
 * "Zip code", while the message maps are keyed "ZIP code".
 */
const findMessageByLabel = ({
  defaultLabel,
  label,
  messagesByLabel,
}: {
  defaultLabel: string;
  label?: string;
  messagesByLabel: Record<string, AddressFormMessage>;
}): AddressFormMessage => {
  const normalised = (label ?? defaultLabel).trim().toLowerCase();
  const match = Object.entries(messagesByLabel).find(
    ([candidate]) => candidate.toLowerCase() === normalised,
  );

  return match ? match[1] : messagesByLabel[defaultLabel];
};

// Each picks the error phrased with the country's own word for the field.
const getEnterCityMessage = (cityLabel?: string): AddressFormMessage =>
  findMessageByLabel({
    defaultLabel: DEFAULT_CITY_LABEL,
    label: cityLabel,
    messagesByLabel: cityMessageByLabel,
  });

const getValidPostalCodeMessage = (
  postalCodeLabel?: string,
): AddressFormMessage =>
  findMessageByLabel({
    defaultLabel: DEFAULT_POSTAL_CODE_LABEL,
    label: postalCodeLabel,
    messagesByLabel: postalCodeMessageByLabel,
  });

const getSelectZoneMessage = (zoneLabel?: string): AddressFormMessage =>
  findMessageByLabel({
    defaultLabel: DEFAULT_ZONE_LABEL,
    label: zoneLabel,
    messagesByLabel: zoneMessageByLabel,
  });

type CreateAddressRulesArgs = {
  /** What this address's fields are called in the consumer's values. */
  fields: AddressFieldNames;
  intl: Pick<intlFormatPropTypes, "formatMessage">;
  /** The country currently picked in the form, from `@shopify/address`. */
  selectedCountry?: Country;
};

/** Reads one field as a string, so an absent or oddly-typed value reads as blank. */
const readField = (values: Record<string, unknown>, name: string): string => {
  const value = values[name];

  return typeof value === "string" ? value : "";
};

/**
 * Validation for one postal address, ready to hand to `superRefine`. A factory
 * because the selected country decides what is required and how the postal code
 * is checked, so rebuild it whenever that changes.
 *
 * Country itself is not checked here — several addresses can share one country
 * field, and checking per address would report the same error twice.
 */
const createAddressRules =
  ({ fields, intl, selectedCountry }: CreateAddressRulesArgs) =>
  (values: Record<string, unknown>, ctx: z.RefinementCtx) => {
    const addIssue = (path: string, message: AddressFormMessage) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path],
        message: intl.formatMessage(message),
      });

    const labels = selectedCountry?.labels;
    const hasZones = (selectedCountry?.zones?.length ?? 0) > 0;

    if (isBlank(readField(values, fields.street))) {
      addIssue(fields.street, addressFormMessages.enterStreetAddress);
    }

    if (isBlank(readField(values, fields.city))) {
      addIssue(fields.city, getEnterCityMessage(labels?.city));
    }

    // Countries without zones (e.g. Singapore) render no zone field at all, so an
    // empty value is correct rather than missing.
    if (hasZones && isBlank(readField(values, fields.zone))) {
      addIssue(fields.zone, getSelectZoneMessage(labels?.zone));
    }

    // `validate` returns `true` or an explanatory string, and rejects an empty
    // postal code on its own — so this covers required-ness as well as format.
    if (
      validatePostalCode(
        readField(values, fields.country),
        readField(values, fields.postalCode),
      ) !== true
    ) {
      addIssue(
        fields.postalCode,
        getValidPostalCodeMessage(labels?.postalCode),
      );
    }
  };

export {
  buildCountryOptions,
  buildZoneOptions,
  createAddressRules,
  findCountryByCode,
  getEnterCityMessage,
  getSelectZoneMessage,
  getValidPostalCodeMessage,
  isBlank,
};

export type { CreateAddressRulesArgs };
